import React, { useEffect } from "react";
import { Stack, IconButton, Typography } from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { PAGE_NAVIGATOR_CONSTANTS } from "../constants";

interface PageNavigatorProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

/**
 * Navigation control for moving between flyer pages.
 *
 * Provides previous/next buttons and displays the current page number out of total pages.
 * Supports keyboard navigation using arrow keys (Left/Right) for quick page switching.
 * Buttons are automatically disabled when at the first or last page to prevent invalid navigation.
 *
 * @param props - Component properties
 * @param props.currentPage - Zero-based index of the currently displayed page
 * @param props.totalPages - Total number of pages available in the flyer
 * @param props.onPageChange - Callback invoked when the user navigates to a different page
 */
export const PageNavigator: React.FC<PageNavigatorProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && currentPage > 0) {
        onPageChange(currentPage - 1);
      } else if (e.key === "ArrowRight" && currentPage < totalPages - 1) {
        onPageChange(currentPage + 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentPage, totalPages, onPageChange]);

  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === totalPages - 1;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      data-testid="flyer-coordinate-page-navigator"
    >
      <IconButton
        onClick={() => onPageChange(currentPage - 1)}
        disabled={isFirstPage}
        size="small"
        data-testid="flyer-coordinate-btn-prev-page"
      >
        <NavigateBeforeIcon />
      </IconButton>

      <Typography
        variant="body2"
        sx={{
          minWidth: PAGE_NAVIGATOR_CONSTANTS.PAGE_LABEL_MIN_WIDTH,
          textAlign: "center",
        }}
        data-testid="flyer-coordinate-page-label"
      >
        Page {currentPage + 1} of {totalPages}
      </Typography>

      <IconButton
        onClick={() => onPageChange(currentPage + 1)}
        disabled={isLastPage}
        size="small"
        data-testid="flyer-coordinate-btn-next-page"
      >
        <NavigateNextIcon />
      </IconButton>
    </Stack>
  );
};
